import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ShoppingBag, ArrowRight } from 'lucide-react';
import { useCartStore } from '@/stores/cartStore';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { useLocalizedPath } from '@/lib/i18n/useLocalizedPath';
import { Button } from '@/components/ui/button';
import { trackFunnelEvent } from '@/hooks/useFunnelTracking';

const exitIntentTranslations: Record<string, {
  title: string;
  subtitle: string;
  cta: string;
  dismiss: string;
  itemsInCart: string;
}> = {
  en: {
    title: "Don't leave your card behind!",
    subtitle: "You've put time into designing your card — complete your order now before your design is lost.",
    cta: "Complete Order",
    dismiss: "Continue browsing",
    itemsInCart: "in your cart",
  },
  de: {
    title: "Deine Karte wartet auf dich!",
    subtitle: "Du hast dir Zeit genommen, deine Karte zu gestalten – schließe deine Bestellung jetzt ab, bevor dein Design verloren geht.",
    cta: "Jetzt bestellen",
    dismiss: "Später weitermachen",
    itemsInCart: "im Warenkorb",
  },
  es: {
    title: "¡No dejes tu tarjeta atrás!",
    subtitle: "Has dedicado tiempo a diseñar tu tarjeta — completa tu pedido ahora antes de que se pierda tu diseño.",
    cta: "Completar pedido",
    dismiss: "Seguir navegando",
    itemsInCart: "en tu carrito",
  },
  fr: {
    title: "N'oublie pas ta carte !",
    subtitle: "Tu as pris le temps de créer ta carte — finalise ta commande maintenant avant que ton design ne soit perdu.",
    cta: "Finaliser la commande",
    dismiss: "Continuer à naviguer",
    itemsInCart: "dans ton panier",
  },
  pt: {
    title: "Não deixe seu cartão para trás!",
    subtitle: "Você dedicou tempo para criar seu cartão — conclua seu pedido agora antes que seu design seja perdido.",
    cta: "Concluir pedido",
    dismiss: "Continuar navegando",
    itemsInCart: "no seu carrinho",
  },
  ru: {
    title: "Не забудьте свою карту!",
    subtitle: "Вы потратили время на оформление карты — завершите заказ сейчас, пока ваш дизайн не потерялся.",
    cta: "Оформить заказ",
    dismiss: "Продолжить просмотр",
    itemsInCart: "в вашей корзине",
  },
  it: {
    title: "Non lasciare la tua carta!",
    subtitle: "Hai dedicato tempo alla creazione della tua carta — completa il tuo ordine ora prima che il tuo design vada perso.",
    cta: "Completa l'ordine",
    dismiss: "Continua a navigare",
    itemsInCart: "nel tuo carrello",
  },
  sv: {
    title: "Glöm inte ditt kort!",
    subtitle: "Du har lagt tid på att designa ditt kort — slutför din beställning nu innan din design går förlorad.",
    cta: "Slutför beställning",
    dismiss: "Fortsätt surfa",
    itemsInCart: "i din varukorg",
  },
  zh: {
    title: "别忘了你的卡片！",
    subtitle: "您花时间设计了卡片——现在完成订单，以免设计丢失。",
    cta: "完成订单",
    dismiss: "继续浏览",
    itemsInCart: "在您的购物车中",
  },
};

const SESSION_KEY = 'exit_intent_shown';

export const ExitIntentPopup: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const hasShownRef = useRef(false);
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { getLocalizedPath } = useLocalizedPath();
  const items = useCartStore((s) => s.items);
  const openCart = useCartStore((s) => s.openCart);

  const t = exitIntentTranslations[language] || exitIntentTranslations.en;

  const showPopup = useCallback(() => {
    if (hasShownRef.current) return;
    if (items.length === 0) return;
    if (sessionStorage.getItem(SESSION_KEY)) return;
    // Never show on admin routes
    if (window.location.pathname.startsWith('/admin')) return;

    hasShownRef.current = true;
    sessionStorage.setItem(SESSION_KEY, '1');
    setIsVisible(true);
    trackFunnelEvent('exit_intent', { items_in_cart: items.length });
  }, [items.length]);

  const dismiss = useCallback(() => {
    setIsVisible(false);
    trackFunnelEvent('exit_intent_dismiss', { items_in_cart: items.length });
  }, [items.length]);

  const goToCheckout = useCallback(() => {
    setIsVisible(false);
    trackFunnelEvent('exit_intent_cta', { items_in_cart: items.length });
    navigate(getLocalizedPath('/checkout'));
  }, [navigate, getLocalizedPath, items.length]);

  // Desktop: mouse leaves viewport toward top
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (e.clientY <= 5) showPopup();
    };
    document.addEventListener('mouseleave', handler);
    return () => document.removeEventListener('mouseleave', handler);
  }, [showPopup]);

  // Mobile exit-intent disabled — too confusing on touch devices (triggers on normal taps)

  if (!isVisible) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      onClick={dismiss}
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Popup */}
      <div
        className="relative w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-2xl animate-in fade-in zoom-in-95 duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <button
          onClick={dismiss}
          className="absolute right-3 top-3 rounded-full p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Icon */}
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
          <ShoppingBag className="h-7 w-7 text-primary" />
        </div>

        {/* Title */}
        <h2 className="text-center text-xl font-bold text-foreground mb-2">
          {t.title}
        </h2>
        <p className="text-center text-sm text-muted-foreground mb-5">
          {t.subtitle}
        </p>

        {/* Cart items preview */}
        <div className="mb-5 space-y-2 max-h-40 overflow-y-auto">
          {items.slice(0, 3).map((item) => (
            <div
              key={item.localId}
              className="flex items-center gap-3 rounded-xl border border-border bg-muted/30 p-2.5"
            >
              {item.productImage && (
                <img
                  src={item.productImage}
                  alt={item.productName}
                  className="h-10 w-14 rounded-lg object-cover"
                />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">
                  {item.customization?.cartDisplayName || item.productName}
                </p>
                <p className="text-xs text-muted-foreground">
                  {item.price.amount} {item.price.currencyCode}
                </p>
              </div>
            </div>
          ))}
          {items.length > 3 && (
            <p className="text-center text-xs text-muted-foreground">
              +{items.length - 3} {t.itemsInCart}
            </p>
          )}
        </div>

        {/* CTA */}
        <Button
          onClick={goToCheckout}
          className="w-full gap-2 text-base font-semibold h-12"
          size="lg"
        >
          {t.cta}
          <ArrowRight className="h-4 w-4" />
        </Button>

        {/* Dismiss link */}
        <button
          onClick={dismiss}
          className="mt-3 block w-full text-center text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          {t.dismiss}
        </button>
      </div>
    </div>
  );
};
